import { useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import Layout from './components/Layout';
import { initPushNotifications, onForegroundMessage } from './lib/pushNotifications';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import VendorPage from './pages/VendorPage';
import SearchPage from './pages/SearchPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderDetailPage from './pages/OrderDetailPage';
import OrdersPage from './pages/OrdersPage';
import ChatPage from './pages/ChatPage';

// Sprint 10: AI-Powered Components
import HomeV2 from './components/home/HomeV2';
// Sprint 15: AI Meal Planner V2 (replaces V1)
import MealPlannerV2 from './components/ai/MealPlannerV2';
import SearchV2 from './components/search/SearchV2';

// Sprint 10T: Tracking V2 & Checkout V2
import TrackingV2 from './components/tracking/TrackingV2';
import CheckoutV2 from './pages/CheckoutV2';

// Feature flags (can be controlled via API)
const USE_V2_HOME = true;
const USE_V2_SEARCH = true;
const USE_V2_TRACKING = true;
const USE_V2_CHECKOUT = false;

// Auth Guard Component
function RequireAuth({ children }: { children: React.ReactElement }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function App() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Frog croak sound for notifications - pre-load on first interaction
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  const playFrogCroak = useCallback(() => {
    try {
      // Create audio if not exists
      if (!audioRef.current) {
        audioRef.current = new Audio('/frog-croak.mp3');
        audioRef.current.volume = 0.7;
      }
      // Reset and play
      audioRef.current.currentTime = 0;
      audioRef.current.play().then(() => {
        console.log('🐸 Frog croak played!');
      }).catch(err => {
        console.log('Could not play notification sound (autoplay blocked?):', err);
      });
    } catch (err) {
      console.log('Audio not supported');
    }
  }, []);

  // Pre-load audio on first user interaction to bypass autoplay restrictions
  useEffect(() => {
    const preloadAudio = () => {
      if (!audioRef.current) {
        audioRef.current = new Audio('/frog-croak.mp3');
        audioRef.current.volume = 0.7;
        // Load the audio
        audioRef.current.load();
        console.log('🐸 Frog croak audio pre-loaded');
      }
    };
    
    // Pre-load on any user interaction
    document.addEventListener('click', preloadAudio, { once: true });
    document.addEventListener('touchstart', preloadAudio, { once: true });
    
    return () => {
      document.removeEventListener('click', preloadAudio);
      document.removeEventListener('touchstart', preloadAudio);
    };
  }, []);

  // Listen for service worker messages (for background notification sounds)
  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      console.log('Service worker message received:', event.data);
      if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') {
        playFrogCroak();
      }
    };
    
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [playFrogCroak]);

  // Initialize push notifications when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    initPushNotifications().then((token) => {
      if (token) {
        console.log('Push notifications enabled for buyer');
      }
    });

    // Listen for foreground messages
    const unsubscribe = onForegroundMessage((payload) => {
      const title = payload.notification?.title || 'Order Update';
      const body = payload.notification?.body || '';
      const data = payload.data || {};
      
      // Play frog croak sound
      playFrogCroak();
      
      // Show browser notification for foreground messages
      if (Notification.permission === 'granted') {
        const notification = new Notification(title, { 
          body, 
          icon: '/frog_logo.png',
          tag: data.orderId || 'order-update',
          silent: true, // We play our own sound
        });
        
        // Navigate to order when notification is clicked
        notification.onclick = () => {
          if (data.orderId) {
            window.location.href = `/orders/${data.orderId}`;
          }
          notification.close();
        };
      }
      
      // If order was cancelled, refresh the current page to show updated status
      if (data.status === 'cancelled' && data.orderId) {
        console.log('Order cancelled - refreshing page');
        // Force reload to get fresh data
        window.location.reload();
      }
    });

    return unsubscribe;
  }, [isAuthenticated]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected routes with Layout (has bottom nav) */}
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        {/* Sprint 10: Use HomeV2 if enabled */}
        <Route path="/" element={USE_V2_HOME ? <HomeV2 /> : <HomePage />} />
        <Route path="/vendors" element={USE_V2_HOME ? <HomeV2 /> : <HomePage />} />
        
        {/* Sprint 10: Use SearchV2 if enabled */}
        <Route path="/search" element={USE_V2_SEARCH ? <SearchV2 /> : <SearchPage />} />
        
        {/* Sprint 15: AI Meal Planner V2 */}
        <Route path="/ai/meal-planner" element={<MealPlannerV2 />} />
        
        <Route path="/cart" element={<CartPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/chat/:threadId" element={<ChatPage />} />
        <Route path="/profile" element={<div>Profile Page</div>} />
      </Route>

      {/* Protected routes without Layout (custom headers) */}
      <Route
        path="/vendor/:id"
        element={
          <RequireAuth>
            <VendorPage />
          </RequireAuth>
        }
      />
      {/* Sprint 10T: Checkout V2 */}
      <Route
        path="/checkout"
        element={
          <RequireAuth>
            {USE_V2_CHECKOUT ? <CheckoutV2 /> : <CheckoutPage />}
          </RequireAuth>
        }
      />
      
      {/* Sprint 10T: Tracking V2 */}
      <Route
        path="/track/:id"
        element={
          <RequireAuth>
            <TrackingV2 />
          </RequireAuth>
        }
      />
      
      {/* Legacy order detail (fallback) */}
      <Route
        path="/order/:id"
        element={
          <RequireAuth>
            {USE_V2_TRACKING ? <TrackingV2 /> : <OrderDetailPage />}
          </RequireAuth>
        }
      />
      
      {/* Order detail alias */}
      <Route
        path="/orders/:id"
        element={
          <RequireAuth>
            {USE_V2_TRACKING ? <TrackingV2 /> : <OrderDetailPage />}
          </RequireAuth>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
